import { ReactNode, useState, useEffect, useRef } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import {
  HardDrive,
  Box,
  LayoutDashboard,
  BookOpen,
  Settings,
  Moon,
  Sun,
  Search,
  LogOut,
  ChevronDown,
  Bell,
  Server,
  Check,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePoolStore } from '@/stores/usePoolStore';
import { useSandboxStore } from '@/stores/useSandboxStore';
import clsx from 'clsx';

interface LayoutProps {
  children?: ReactNode;
}

const navigation = [
  { name: 'Home', to: '/home', icon: LayoutDashboard },
  { name: 'Sandboxes', to: '/sandboxes', icon: Box },
  { name: 'Images', to: '/images', icon: HardDrive },
  { name: 'Docs', to: '/docs', icon: BookOpen },
  { name: 'Settings', to: '/settings', icon: Settings },
];

export function Layout({ children }: LayoutProps) {
  const { theme, toggleTheme } = useTheme();
  const { user, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const { pools, selectedPoolIds, fetchPools, togglePool, selectAll, getSelectedPools } = usePoolStore();
  const setCurrentUser = useSandboxStore(state => state.setCurrentUser);
  const selectedPools = getSelectedPools();

  // Build trigger label for pool selector
  const poolTriggerLabel = (() => {
    if (selectedPools.length === 0) return 'Select pool';
    if (selectedPools.length === pools.length && pools.length > 1) return 'All pools';
    if (selectedPools.length === 1) return selectedPools[0].displayName;
    return `${selectedPools.length} pools`;
  })();

  // Pool dropdown state
  const [poolOpen, setPoolOpen] = useState(false);
  const poolRef = useRef<HTMLDivElement>(null);

  // User menu state
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);

  // Set current user and fetch pools after auth is fully settled
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (user?.objectId) {
        setCurrentUser(user.objectId);
      }
      fetchPools();
    }
  }, [isAuthenticated, authLoading, user?.objectId, setCurrentUser, fetchPools]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (poolRef.current && !poolRef.current.contains(e.target as Node)) {
        setPoolOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className="flex h-screen bg-surface-50 dark:bg-[#0a0a0a]">
      {/* ─── Icon Rail ─── */}
      <aside className="icon-rail">
        {/* Brand mark */}
        <div className="flex items-center justify-center h-12 flex-shrink-0">
          <div className="w-8 h-8 rounded-fluent-md bg-brand-500 flex items-center justify-center">
            <span className="text-white text-sm font-bold">W</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col gap-1 py-2">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.to}
              end={item.to === '/home'}
              className={({ isActive }) =>
                clsx(
                  'icon-rail-item',
                  isActive && 'icon-rail-item-active'
                )
              }
              title={item.name}
            >
              {({ isActive }) => (
                <>
                  <item.icon className={clsx('w-5 h-5', isActive && 'stroke-[2.5]')} />
                  {!isActive && <span className="icon-rail-label">{item.name}</span>}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Footer — theme toggle */}
        <div className="flex flex-col items-center gap-1 py-2 border-t border-surface-200 dark:border-white/[0.06]">
          <button
            onClick={toggleTheme}
            className="icon-rail-item"
            aria-label="Toggle theme"
            title={theme === 'light' ? 'Dark mode' : 'Light mode'}
          >
            {theme === 'light' ? (
              <Moon className="w-5 h-5" />
            ) : (
              <Sun className="w-5 h-5" />
            )}
          </button>
        </div>
      </aside>

      {/* ─── Top Bar ─── */}
      <header className="top-bar">
        {/* App title + pool selector */}
        <div className="flex items-center gap-3 min-w-0">
          <h1 className="text-sm font-semibold text-surface-900 dark:text-surface-100 whitespace-nowrap">
            Windows 365A
          </h1>

          <div className="w-px h-5 bg-surface-200 dark:bg-white/[0.08]" />

          {/* Pool dropdown */}
          <div ref={poolRef} className="relative">
            <button
              onClick={() => setPoolOpen(!poolOpen)}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-fluent-md text-sm transition-all duration-150',
                'text-surface-600 dark:text-surface-300',
                'hover:bg-surface-100 dark:hover:bg-white/[0.06]',
                poolOpen && 'bg-surface-100 dark:bg-white/[0.06]'
              )}
            >
              <Server className="w-3.5 h-3.5 text-brand-500" />
              <span className="font-medium">{poolTriggerLabel}</span>
              <ChevronDown className={clsx(
                'w-3.5 h-3.5 text-surface-400 transition-transform duration-150',
                poolOpen && 'rotate-180'
              )} />
            </button>

            {/* Pool dropdown menu */}
            {poolOpen && (
              <div className={clsx(
                'absolute top-full left-0 mt-1 w-56 z-50',
                'bg-white dark:bg-[#2a2a2a] rounded-fluent-lg',
                'border border-surface-200 dark:border-white/[0.08]',
                'shadow-elevation-8 dark:shadow-none',
                'py-1 animate-fade-in'
              )}>
                {/* Select All option */}
                {pools.length > 1 && (
                  <>
                    <button
                      onClick={() => selectAll()}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                        'hover:bg-surface-50 dark:hover:bg-white/[0.04]',
                        selectedPoolIds.length === pools.length && 'text-brand-500'
                      )}
                    >
                      <div className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                        selectedPoolIds.length === pools.length
                          ? 'bg-brand-500 border-brand-500'
                          : 'border-surface-300 dark:border-white/[0.1]'
                      )}>
                        {selectedPoolIds.length === pools.length && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="font-medium">Select All</span>
                    </button>
                    <div className="mx-3 my-1 border-t border-surface-100 dark:border-white/[0.06]" />
                  </>
                )}
                {pools.map((pool) => {
                  const isSelected = selectedPoolIds.includes(pool.poolId);
                  return (
                    <button
                      key={pool.poolId}
                      onClick={() => togglePool(pool.poolId)}
                      className={clsx(
                        'w-full flex items-center gap-3 px-3 py-2 text-sm text-left transition-colors',
                        'hover:bg-surface-50 dark:hover:bg-white/[0.04]',
                        isSelected && 'text-brand-500'
                      )}
                    >
                      {/* Checkbox */}
                      <div className={clsx(
                        'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors',
                        isSelected
                          ? 'bg-brand-500 border-brand-500'
                          : 'border-surface-300 dark:border-white/[0.1]'
                      )}>
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Server className="w-4 h-4 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{pool.displayName}</p>
                        <p className="text-xs text-surface-400 truncate">{pool.region}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side: search + notifications + user */}
        <div className="flex items-center gap-1">
          {/* Search */}
          <button
            className="p-2 rounded-fluent text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-white/[0.06] transition-colors"
            title="Search"
          >
            <Search className="w-4 h-4" />
          </button>

          {/* Notifications placeholder */}
          <button
            className="p-2 rounded-fluent text-surface-500 dark:text-surface-400 hover:bg-surface-100 dark:hover:bg-white/[0.06] transition-colors"
            title="Notifications"
          >
            <Bell className="w-4 h-4" />
          </button>

          <div className="w-px h-5 bg-surface-200 dark:bg-white/[0.08] mx-1" />

          {/* User avatar + dropdown */}
          {user && (
            <div ref={userRef} className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className={clsx(
                  'flex items-center gap-2 px-2 py-1.5 rounded-fluent-md transition-colors',
                  'hover:bg-surface-100 dark:hover:bg-white/[0.06]',
                  userMenuOpen && 'bg-surface-100 dark:bg-white/[0.06]'
                )}
              >
                <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[11px] font-semibold">{user.initials}</span>
                </div>
                <span className="text-sm font-medium text-surface-700 dark:text-surface-200 hidden sm:block max-w-[120px] truncate">
                  {user.name}
                </span>
              </button>

              {/* User dropdown */}
              {userMenuOpen && (
                <div className={clsx(
                  'absolute top-full right-0 mt-1 w-56 z-50',
                  'bg-white dark:bg-[#2a2a2a] rounded-fluent-lg',
                  'border border-surface-200 dark:border-white/[0.08]',
                  'shadow-elevation-8 dark:shadow-none',
                  'py-1 animate-fade-in'
                )}>
                  <div className="px-3 py-2 border-b border-surface-100 dark:border-white/[0.06]">
                    <p className="text-sm font-medium text-surface-900 dark:text-surface-100">{user.name}</p>
                    <p className="text-xs text-surface-400 truncate">{user.email}</p>
                  </div>
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-surface-600 dark:text-surface-300 hover:bg-surface-50 dark:hover:bg-white/[0.04] transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ─── Main Content ─── */}
      <main className="flex-1 overflow-auto scrollbar-thin ml-14 mt-12">
        {children || <Outlet />}
      </main>
    </div>
  );
}
